import { CSSProperties, ReactNode, forwardRef } from 'react';
import cn from 'classnames';
import { Typography } from '@scale/scaleui';
import { twMerge } from 'tailwind-merge';
import ResponseTemplatingPopper from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseTemplatingPopper';
import { ResponseTemplatePopperMetadata } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseTemplateCreator/ResponseTemplateCreatorStep';

export type ResponseTemplatingProps = {
  showResponseTemplatingIcon: boolean;
  childId: string;
  metadata: ResponseTemplatePopperMetadata;
};

export type CardProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  icon?: ReactNode;
  className?: string;
  contentStyles?: CSSProperties;
  children?: ReactNode;
  onClick?: () => void;
  responseTemplatingProps?: ResponseTemplatingProps;
  customHeader?: ReactNode;
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  // eslint-disable-next-line complexity
  (
    {
      title,
      subtitle,
      footer,
      icon,
      children,
      className,
      onClick,
      responseTemplatingProps,
      contentStyles = {},
      customHeader,
    },
    ref,
  ) => {
    const {
      showResponseTemplatingIcon,
      childId: responseTemplatingChildId,
      metadata,
    } = responseTemplatingProps || {
      showResponseTemplatingIcon: false,
      childId: '',
      metadata: {},
    };

    return (
      <div
        ref={ref}
        className={twMerge(
          cn(
            'w-full text-left border border-neutral-200 rounded-lg h-fit scaleui bg-neutral-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400 transition-colors',
            className,
          ),
        )}
        onClick={onClick}
      >
        {customHeader || (
          <>
            {(title || subtitle) && (
              <div className="flex items-center justify-between w-full px-4 py-3 bg-neutral-50 rounded-t-lg">
                <div className="flex items-center w-full">
                  {icon && <div className="mr-4 rounded bg-primary-50">{icon}</div>}
                  <div className="w-full">
                    {title && (
                      <Typography.Subtitle1 as="div" className="break-all">
                        {title}
                      </Typography.Subtitle1>
                    )}
                    {subtitle && (
                      <Typography.Subtitle4 color="text-neutral-500" as="div" className="break-all">
                        {subtitle}
                      </Typography.Subtitle4>
                    )}
                  </div>
                </div>
                {showResponseTemplatingIcon && (
                  <div className="flex z-30 ml-2">
                    <ResponseTemplatingPopper
                      childId={responseTemplatingChildId}
                      metadata={metadata}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {!!children && (
          <div className={cn('py-3 px-4 border-t border-neutral-100')} style={contentStyles}>
            {children}
          </div>
        )}
        {footer && (
          <div className="flex items-center w-full px-4 py-3 border-t border-neutral-100">
            {footer}
          </div>
        )}
      </div>
    );
  },
);
